/**
 * Timer service - Manages pomodoro timer state and events
 */

import {
  getCurrentPeriod,
  getNextBoundary,
  getTimeUntilBoundary
} from '../utils/time.js';
import type { TimerState } from '../models/types.js';

export type TimerEventType = 'tick' | 'boundary';

export interface TimerEvent {
  type: TimerEventType;
  state: TimerState;
}

class TimerService {
  private intervalId: number | null = null;
  private listeners: Set<(event: TimerEvent) => void> = new Set();
  private lastBoundary: number = 0;
  private running: boolean = false;

  /**
   * Start the timer
   */
  start(): void {
    if (this.running) return;

    console.log('Starting timer service...');
    this.running = true;
    this.lastBoundary = this.getNextBoundary().getTime();

    // Update immediately
    this.tick();

    // Update every second
    this.intervalId = window.setInterval(() => {
      this.tick();
    }, 1000);
  }

  /**
   * Stop the timer
   */
  stop(): void {
    if (!this.running) return;

    console.log('Stopping timer service...');
    this.running = false;

    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Timer tick - calculate state and emit events
   */
  private tick(): void {
    const now = new Date();
    const state = this.getState(now);

    // Check if we've crossed a boundary
    const nextBoundary = this.getNextBoundary(now);
    if (nextBoundary.getTime() !== this.lastBoundary) {
      // Boundary crossed!
      console.log('Timer boundary reached:', nextBoundary);
      this.lastBoundary = nextBoundary.getTime();
      this.emitEvent({ type: 'boundary', state });
    } else {
      // Regular tick
      this.emitEvent({ type: 'tick', state });
    }
  }

  /**
   * Calculate current timer state
   */
  private getState(now: Date = new Date()): TimerState {
    const currentPeriod = getCurrentPeriod(now);
    const nextBoundary = getNextBoundary(now);
    const timeRemaining = getTimeUntilBoundary(now);

    return {
      currentTime: now,
      currentPeriod,
      nextBoundary,
      timeRemaining,
      isActivity: currentPeriod.type === 'activity'
    };
  }

  /**
   * Get next boundary time
   */
  private getNextBoundary(now: Date = new Date()): Date {
    return getNextBoundary(now);
  }

  /**
   * Get current timer state (public)
   */
  getCurrentState(): TimerState {
    return this.getState();
  }

  /**
   * Subscribe to timer events
   */
  subscribe(listener: (event: TimerEvent) => void): () => void {
    this.listeners.add(listener);

    // Immediately call with current state
    listener({ type: 'tick', state: this.getCurrentState() });

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Emit event to all listeners
   */
  private emitEvent(event: TimerEvent): void {
    this.listeners.forEach(listener => listener(event));
  }

  /**
   * Check if timer is running
   */
  isRunning(): boolean {
    return this.running;
  }
}

// Export singleton instance
export const timerService = new TimerService();
export default timerService;
